import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { ChevronDown, Check, Plus } from 'lucide-react';

const API = '/api';

/**
 * CompanySelect — typeahead picker over the CRM's known pharma/investor/profile
 * company names. By default returns just a string via `onChange(name)`.
 *
 * When `onPickRich` is supplied, the component switches to the
 * `/pharma-companies/names-with-country` endpoint and additionally invokes
 * `onPickRich({ name, country })` whenever the user selects (or types-and-adds)
 * a value — letting parent forms auto-fill an adjacent Country field.
 */
export function CompanySelect({ value, onChange, onPickRich, placeholder = "Select company", testId = "company-select" }) {
  const [open, setOpen] = useState(false);
  // `records` is the source of truth. Each entry is { name, country }.
  // For the legacy (string-only) endpoint we synthesize country = ''.
  const [records, setRecords] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const useRich = typeof onPickRich === 'function';

  useEffect(() => {
    if (open) {
      const url = useRich ? `${API}/pharma-companies/names-with-country` : `${API}/pharma-companies/names`;
      axios.get(url, { withCredentials: true })
        .then(res => {
          const data = res.data || [];
          if (useRich) {
            setRecords(Array.isArray(data) ? data : []);
          } else {
            setRecords((Array.isArray(data) ? data : []).map(n => ({ name: n, country: '' })));
          }
        })
        .catch(() => {});
      if (value) setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [open, value, useRich]);

  const emitPick = (record) => {
    onChange(record.name);
    if (useRich) onPickRich({ name: record.name, country: record.country || '' });
  };

  const handleSelect = (name) => {
    const rec = records.find(r => r.name === name) || { name, country: '' };
    emitPick(rec);
    setOpen(false);
    setInputValue('');
  };

  const handleAddNew = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const rec = { name: trimmed, country: '' };
    emitPick(rec);
    setRecords(prev => [...prev, rec].sort((a, b) => a.name.localeCompare(b.name)));
    setOpen(false);
    setInputValue('');
  };

  const isNewEntry = inputValue.trim() && !records.some(r => r.name.toLowerCase() === inputValue.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 text-sm font-normal"
          data-testid={testId}
        >
          <span className={value ? "text-slate-900 truncate" : "text-slate-500"}>
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type new..."
            className="h-9"
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {inputValue.trim() ? (
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-sky-700 hover:bg-sky-50 cursor-pointer"
                  onClick={handleAddNew}
                >
                  <Plus className="w-3.5 h-3.5" /> Add "{inputValue.trim()}"
                </button>
              ) : (
                <span className="text-xs text-slate-400">No companies yet</span>
              )}
            </CommandEmpty>
            <CommandGroup className="max-h-[250px] overflow-y-auto">
              {value && (
                <CommandItem onSelect={() => { onChange(''); if (useRich) onPickRich({ name: '', country: '' }); setOpen(false); setInputValue(''); }} className="text-slate-400 text-xs">
                  Clear selection
                </CommandItem>
              )}
              {isNewEntry && (
                <CommandItem onSelect={handleAddNew} className="text-sky-700">
                  <Plus className="mr-2 h-3.5 w-3.5" /> Add "{inputValue.trim()}"
                </CommandItem>
              )}
              {records.map((r) => (
                <CommandItem key={r.name} value={r.name} onSelect={() => handleSelect(r.name)}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === r.name ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="flex-1 truncate">{r.name}</span>
                  {useRich && r.country && (
                    <span className="text-[10px] text-slate-400 ml-2 shrink-0">{r.country}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
